import { gql, useMutation, useQuery } from '@apollo/client';
import { CvlApplicationStatusEnumType, InvestmentEntityTypeEnumType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';

import { FormInput, FormPanel } from 'components/FormPanel';

import Select from 'primitives/Select';

const REGISTER_USER_ON_CVL_MUTATION = gql(`
  mutation RegisterUserOnCvl(
    $dob: String!
    $email: String!
    $investorCategory: String!
    $mobile: String!
    $name: String!
    $pan: String!
    $typeOfInvestor: String!
    $validityPeriodOfAccreditation: String!
    $investmentEntityId: ID!
  ) {
    registerUserOnCvl(
      dob: $dob
      email: $email
      investorCategory: $investorCategory
      mobile: $mobile
      name: $name
      pan: $pan
      typeOfInvestor: $typeOfInvestor
      validityPeriodOfAccreditation: $validityPeriodOfAccreditation
      investmentEntityId: $investmentEntityId
    ) {
      status
    }
  }
`);

const CVL_INVESTOR_APPLICATION_QUERY = gql(`
  query InvestorAccreditationApplication($investmentEntityId: ID!) {
    investorAccreditationApplication(investmentEntityId: $investmentEntityId) {
      id
      investmentEntityId
      status
      pincode
      financialDetails {
        annualIncomeYear1
        financialAssetsYear1
        netWorthYear1
        qualificationCriteria
      }
      documents {
        id
      }
    }
  }
`);

type Props = {
  investmentEntities: any[];
  investmentEntityId: string;
  onInvestmentEntityChange: (id: string) => void;
  selectedInvestmentEntity: any | null;
  onNextStep: (options?: {
    skipPersonalDetails?: boolean;
    skipFinancialDetails?: boolean;
    skipDocuments?: boolean;
    goToSubmitStep?: boolean;
  }) => void;
  locked?: boolean;
};

const UserRegistrationStep: FC<Props> = ({
  investmentEntities,
  investmentEntityId,
  onInvestmentEntityChange,
  selectedInvestmentEntity,
  onNextStep,
  locked,
}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [registerUserOnCvl, { loading, error }] = useMutation(REGISTER_USER_ON_CVL_MUTATION);

  const {
    data: cvlData,
    loading: cvlLoading,
    refetch: refetchCvlApplication,
  } = useQuery(CVL_INVESTOR_APPLICATION_QUERY, {
    variables: { investmentEntityId },
    skip: !investmentEntityId,
    fetchPolicy: 'network-only',
  });

  const selectedEntity = selectedInvestmentEntity;

  const cvlApplication = cvlData?.investorAccreditationApplication;
  // Registration is complete whenever a CVL application exists for this entity.
  // Do not rely on status === 0: the backend may advance status after registration.
  const hasCvlSuccess =
    !!cvlApplication?.id &&
    String(cvlApplication.investmentEntityId) === String(investmentEntityId);
  const shouldShowSuccess = isSuccess || hasCvlSuccess;
  const status = cvlApplication?.status;
  const isSubmittedSuccessfully = status === CvlApplicationStatusEnumType.ApplicationVerified;
  const isMovedToSubmitStep = status === CvlApplicationStatusEnumType.ApplicationSubmitted;

  const isLocked = locked || isMovedToSubmitStep || isSubmittedSuccessfully;
  const defaultValues = useMemo(() => {
    if (!selectedEntity) {
      return {
        typeOfInvestor: '',
        investorCategory: '',
        name: '',
        dob: '',
        pan: '',
        mobile: '',
        email: '',
        ofOfYearsForAccreditation: '',
      };
    }
    const dob = selectedEntity.kyc?.dateOfBirth ? dayjs(selectedEntity.kyc.dateOfBirth) : '';
    return {
      typeOfInvestor: '',
      investorCategory: selectedEntity.type === InvestmentEntityTypeEnumType.Individual ? '1' : '',
      name: selectedEntity.name ?? '',
      dob,
      pan: selectedEntity.kyc?.pan ?? '',
      mobile: selectedEntity.user?.phoneNumber ?? '',
      email: selectedEntity.user?.email ?? '',
      ofOfYearsForAccreditation: '',
    };
  }, [selectedEntity]);

  return (
    <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-700">
      <h3 className="mb-2 font-semibold">User Registration</h3>
      <p className="mb-4">
        Please fill in the following details to register for the accreditation process.
      </p>
      <div className="mb-4">
        <div className={isLocked ? 'pointer-events-none opacity-60' : undefined}>
          <Select
            label="Investment Entity"
            options={investmentEntities.map(investmentEntity => ({
              label: investmentEntity?.name,
              value: investmentEntity?.id,
              disabled: investmentEntity.status !== 'ACTIVE',
              labelSecondaryText: investmentEntity.status === 'ACTIVE' ? undefined : 'Inactive',
            }))}
            onChange={value => {
              if (isLocked) return;
              setIsSuccess(false);
              onInvestmentEntityChange(value);
            }}
            value={investmentEntityId}
            className="w-full"
          />
        </div>
      </div>
      {cvlLoading ? (
        <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-600">
          Checking your registration status...
        </div>
      ) : isSubmittedSuccessfully ? (
        <div className="rounded-md bg-green-50 p-4 text-sm text-green-800">
          <h4 className="mb-1 font-semibold">Application submitted successfully</h4>
          <p>Your application has been submitted successfully. Details are now locked.</p>
          <button
            type="button"
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={() => onNextStep({ goToSubmitStep: true })}
          >
            View submission status
          </button>
        </div>
      ) : isMovedToSubmitStep || locked ? (
        <div className="rounded-md border border-gray-200 bg-white p-4 text-sm text-gray-700">
          <h4 className="mb-1 font-semibold">Continue to submit application</h4>
          <p className="text-sm text-gray-600">
            Your application is ready to be submitted. Previous steps are now locked.
          </p>
          <button
            type="button"
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={() => onNextStep({ goToSubmitStep: true })}
          >
            Go to submit application
          </button>
        </div>
      ) : shouldShowSuccess ? (
        <div className="rounded-md bg-green-50 p-4 text-sm text-green-800">
          <h4 className="mb-1 font-semibold">Registration successful</h4>
          <p>Your details have been submitted for registration successfully.</p>
          <button
            type="button"
            className="mt-4 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            onClick={() => {
              if (
                status === CvlApplicationStatusEnumType.ApplicationSubmitted ||
                status === CvlApplicationStatusEnumType.ApplicationVerified ||
                status === CvlApplicationStatusEnumType.ApplicationApproved ||
                status === CvlApplicationStatusEnumType.ApplicationRejected
              ) {
                onNextStep({ goToSubmitStep: true });
                return;
              }
              onNextStep(
                cvlApplication
                  ? {
                      ...(cvlApplication.pincode ? { skipPersonalDetails: true } : {}),
                      ...(cvlApplication.financialDetails ? { skipFinancialDetails: true } : {}),
                      ...(cvlApplication.documents && cvlApplication.documents.length
                        ? { skipDocuments: true }
                        : {}),
                    }
                  : undefined
              );
            }}
          >
            Continue to next step
          </button>
        </div>
      ) : (
        <FormPanel
          key={investmentEntityId}
          onSubmit={async data => {
            try {
              const result = await registerUserOnCvl({
                variables: {
                  ...data,
                  dob: data.dob ? dayjs(data.dob).format('DD/MM/YYYY') : '',
                  investmentEntityId: investmentEntityId,
                },
              });

              if (result.errors?.length) {
                return;
              }

              // Success is indicated by a non-null payload; status may not be 0 after API changes.
              const registered = result.data?.registerUserOnCvl;
              if (registered == null) {
                return;
              }

              setIsSuccess(true);

              let app = cvlData?.investorAccreditationApplication;
              try {
                const { data: refetched } = await refetchCvlApplication();
                app = refetched?.investorAccreditationApplication ?? app;
              } catch {
                // Still advance; skip flags may be incomplete until next load
              }

              onNextStep(
                app
                  ? {
                      ...(app.pincode ? { skipPersonalDetails: true } : {}),
                      ...(app.financialDetails ? { skipFinancialDetails: true } : {}),
                      ...(app.documents && app.documents.length ? { skipDocuments: true } : {}),
                    }
                  : undefined
              );
            } catch {
              // Network / thrown errors are surfaced via mutation `error`
            }
          }}
          loading={loading}
          error={error}
          buttonRef={null}
        >
          <FormInput
            fieldName="typeOfInvestor"
            label="Type of Investor"
            type="select"
            placeholder="Select the type of investor"
            defaultValue={defaultValues.typeOfInvestor}
            options={[{ value: '1', label: 'Indian Resident' }]}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="investorCategory"
            label="Investor Category"
            type="select"
            placeholder="Select the investor category"
            defaultValue={defaultValues.investorCategory}
            options={[{ value: '1', label: 'Individual' }]}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="name"
            label="Name"
            type="text"
            placeholder="Enter your name"
            defaultValue={defaultValues.name}
            validators={{ required: true, isAlphabetic: true }}
          />
          <FormInput
            fieldName="dob"
            label="Date of Birth"
            type="date"
            placeholder="Enter your date of birth"
            defaultValue={defaultValues.dob}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="pan"
            label="Permanent Account Number"
            type="text"
            placeholder="Enter your Permanent Account Number"
            defaultValue={defaultValues.pan}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="mobile"
            label="Mobile Number"
            type="text"
            placeholder="Enter your mobile number"
            defaultValue={defaultValues.mobile}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="email"
            label="Email"
            type="text"
            placeholder="Enter your email"
            defaultValue={defaultValues.email}
            validators={{ required: true }}
          />
          <FormInput
            fieldName="validityPeriodOfAccreditation"
            label="Select the validity period for your accreditation"
            type="select"
            placeholder="Select the validity period for your accreditation"
            helperText="Cost of accreditation varies as per selected validity period"
            defaultValue={defaultValues.ofOfYearsForAccreditation}
            options={[
              { value: '1', label: '2 years' },
              { value: '2', label: '3 years' },
            ]}
            validators={{ required: true }}
          />
        </FormPanel>
      )}
    </div>
  );
};

export default UserRegistrationStep;
